import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';

import EntityInformationTableContent from 'components/EntityInformationTable/EntityInformationTableContent';
import GqlClientProvider from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/GqlClientProvider';
import CardContext from 'contexts/CardContext';
import MetricOptionsContext from 'contexts/MetricOptionsContext';
import useGetFieldLabel from 'hooks/useGetFieldLabel';
import Typography from 'kingpin/atoms/Typography';
import EntityDetailsContext from 'screens/EntityDetailsShow/EntityDetailsContext';

import buildEntityInformationTableDefinition from './buildEntityInformationTableDefinition';
import ChartPlaceholder from './ChartPlaceholder';
import LegacyGadgetBody from './LegacyGadgetBody';
import StudioContentTypeContext from './StudioContentTypeContext';

const Rows = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const RowLabel = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 4px;
  background-color: var(--color-bg-secondary);
`;

// Without an entity to show in Studio (not previewing one), the rows it
// would show. On a dashboard or report the legacy gadget's own empty state
// shows instead, as it does on a Classic card.
const EntityDetailsOutline = ({
  cardId,
  rows,
}: {
  cardId: string;
  rows: VisualisationDefinitions.EntityInformationTableRow[];
}) => {
  const { metricOptionsLookup } = useContext(MetricOptionsContext);
  const { getFieldLabel } = useGetFieldLabel();

  return (
    <Rows data-testid={`studio-entity-details-outline-${cardId}`}>
      <Typography.Body type="Body 12" color="var(--color-text-tertiary)">
        Shows an entity's values on its page. Preview as one (top bar) to see
        them here.
      </Typography.Body>
      {rows.map((row) => (
        <RowLabel key={row.key}>
          <Typography.Body type="Body 13">
            {row.type === 'field'
              ? getFieldLabel({ field: row.field, dataType: row.datasetId })
              : (metricOptionsLookup[row.metricId] || { name: 'Metric' }).name}
          </Typography.Body>
          <Typography.Body type="Body 12" color="var(--color-text-tertiary)">
            {row.type === 'metric' && row.trending ? 'Metric, trended' : ''}
            {row.type === 'metric' && !row.trending ? 'Metric' : ''}
            {row.type === 'field' ? 'Field' : ''}
          </Typography.Body>
        </RowLabel>
      ))}
    </Rows>
  );
};

// An entity details Table (a block, or a Composite's element): drawn by the
// legacy Entity Information Table it saves as, for the entity page's entity,
// within its dates and filters. Stable while its settings don't change, so
// it doesn't refetch on a move. w and h are the grid size of the block it
// sits in.
const StudioEntityDetails = ({
  content,
  name,
  w,
  h,
  isInteractive,
  isOnView = false,
}: {
  content: Studio.ChartContent;
  name: string;
  w: number;
  h: number;
  isInteractive: boolean;
  // On a dashboard or report, rather than in Studio
  isOnView?: boolean;
}) => {
  const contentType = useContext(StudioContentTypeContext);
  const entityDetails = useContext(EntityDetailsContext);
  const settingsKey = JSON.stringify(content.table?.entityDetails ?? {});
  const built = useMemo(
    () =>
      buildEntityInformationTableDefinition({
        card: {
          visType: 'table',
          description: content.description,
          table: {
            rows: 'entityDetails',
            entityDetails: JSON.parse(settingsKey),
          },
        },
        id: content.id,
        name,
        isReport: contentType === 'report',
      }),
    // The settings are compared by value
    [content.description, content.id, contentType, name, settingsKey],
  );
  const gadget = 'chartDefinition' in built ? built.chartDefinition : undefined;
  const cardContextValue = useMemo(
    () => ({ i: content.id, w, h, chartDefinition: gadget }),
    [content.id, gadget, h, w],
  );

  if (!gadget) {
    return (
      <ChartPlaceholder
        cardId={content.id}
        visType="table"
        message={'missing' in built ? built.missing : ''}
      />
    );
  }
  return (
    <LegacyGadgetBody
      $isInteractive={isInteractive}
      data-testid={`studio-card-entity-details-${content.id}`}
    >
      <div>
        {entityDetails || isOnView ? (
          <CardContext.Provider value={cardContextValue}>
            <GqlClientProvider
              visName={gadget.name}
              visId={gadget.id}
              visType={gadget.type}
            >
              <EntityInformationTableContent gadget={gadget} />
            </GqlClientProvider>
          </CardContext.Provider>
        ) : (
          <EntityDetailsOutline cardId={content.id} rows={gadget.rows} />
        )}
      </div>
    </LegacyGadgetBody>
  );
};

export default StudioEntityDetails;
